import { useEffect, useState } from 'react';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { useSplToken } from '../hooks/useSplToken';
import { raydiumAuthorityV4 } from '../utils/constants';
import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { useAutomatizationGateway } from '../hooks/useAutomatizationGateway';
import { ClientCallback, ClientLog, Event, LogLevel, Role } from '@sodamnfoolish/spl-factory-backend-automatization';
import { freezerJitoTip } from '../../ConfigFees';

export function Freezer({
    connection,
    deployedToken,
    freezer,
    allWallets,
    volumeBuyWallets,
    logMessage,
    injectToken,
    updatedAt,
    autoAction,
}: {
    connection: Connection;
    deployedToken: string;
    freezer: Keypair;
    allWallets: string[];
    volumeBuyWallets: string[];
    logMessage: (message: string, type?: 'success' | 'error') => void;
    injectToken: (address: string) => Promise<void>;
    updatedAt: string;
    autoAction: string;
}) {
    const {
        freezeFutureAccounts,
        getWalletTokenAccountByMint,
        freezeLpOwnersWithWhitelist,
        freezeTokenAccountsWithJito,
        thawTokenAccountsWithJito,
        freezeAllTokenAccountsWithWhitelist,
    } = useSplToken();

    const { automatizationRole, automatizationClient } = useAutomatizationGateway((role) => role == Role.Freezer || role == Role.LPFreezer);

    const [freezeUnfreezeInput, setFreezeUnfreezeInput] = useState<string>('');

    const [freezeStarted, setFreezeStarted] = useState<boolean>(false);
    const [smartFreezeStarted, setSmartFreezeStarted] = useState<boolean>(false);
    const [lpFreezeStarted, setLPFreezeStarted] = useState<boolean>(false);

    const [freezerBalance, setFreezerBalance] = useState<number>(0);

    const freezeUnfreezeInputChange = (event: { target: { value: string } }) => {
        setFreezeUnfreezeInput(event.target.value);
    };

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);
    useEffect(() => {
        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        if (!autoActionStarted && timeDifference < 30000) {
            if (autoAction === 'smartFreezer') {
                logMessage('Starting auto action for Smart Freezer.');
                setAutoActionStarted(true);
                startSmartFreezes(connection, allWallets.concat(volumeBuyWallets), freezer, deployedToken);
            } else if (autoAction === 'lpFreezer') {
                logMessage('Starting auto action for LP Freezer.');
                setAutoActionStarted(true);
                startLPFreezes(connection, allWallets.concat(volumeBuyWallets), freezer, deployedToken);
            }
        }
    }, [deployedToken, autoAction]);

    useEffect(() => {
        try {
            if (freezer) {
                connection.getBalance(freezer.publicKey, 'processed').then((balance) => {
                    setFreezerBalance(balance / LAMPORTS_PER_SOL);
                });

                const subId = connection.onAccountChange(
                    freezer.publicKey,
                    (account) => {
                        const balance = account.lamports / LAMPORTS_PER_SOL;
                        setFreezerBalance(balance);
                        if (balance < 0.1) {
                            logMessage('Freezer balance is low!', 'error');
                        }
                    },
                    'processed',
                );
                return () => {
                    connection.removeAccountChangeListener(subId);
                };
            }
        } catch (error) {
            console.log(error);
        }
    }, [freezer]);

    useEffect(() => {
        if (!automatizationClient) return;

        automatizationClient.on(Event.PreStart, async (address: string, callback: ClientCallback) => {
            await injectToken(address).catch((reason) => {
                callback([new ClientLog(LogLevel.Error, `Error`)]);
                throw reason;
            });
            callback([new ClientLog(LogLevel.Info, `Address received ${address}`)]);
        });

        automatizationClient.on(Event.StartFreezer, (_: string, callback: ClientCallback) => {
            const fn =
                automatizationRole == Role.Freezer
                    ? () => startSmartFreezes(connection, allWallets.concat(volumeBuyWallets), freezer, deployedToken)
                    : () => startLPFreezes(connection, allWallets.concat(volumeBuyWallets), freezer, deployedToken);
            fn().catch((reason) => {
                callback([new ClientLog(LogLevel.Error, 'Error')]);
                throw reason;
            });
            setTimeout(() => callback([new ClientLog(LogLevel.Info, 'Started')]), 1000);
        });

        automatizationClient.on(Event.StopFreezer, (_: string, callback: ClientCallback) => {
            callback([new ClientLog(LogLevel.Info, 'Stopped')]);
            window.location.reload();
        });

        return () => {
            automatizationClient.off(Event.PreStart);
        };
    }, [automatizationClient]);

    async function startFreezes() {
        if (freezeStarted) {
            logMessage('Freezing already started!', 'error');
            return;
        }

        setFreezeStarted(true);
        freezeFutureAccounts(connection, freezer, deployedToken, freezerJitoTip, logMessage);
    }

    async function startSmartFreezes(connection: Connection, whitelist: string[], freezer: Keypair, deployedToken: string) {
        if (smartFreezeStarted) {
            logMessage('Smart freezing already started!', 'error');
            return;
        }

        if (!freezer) {
            logMessage('No freezer found', 'error');
            return;
        }

        setSmartFreezeStarted(true);

        await freezeAllTokenAccountsWithWhitelist(connection, whitelist, freezer, deployedToken, freezerJitoTip, logMessage);
    }

    async function startLPFreezes(connection: Connection, whitelist: string[], freezer: Keypair, deployedToken: string) {
        if (lpFreezeStarted) {
            logMessage('LP freezing already started!', 'error');
            return;
        }

        if (!freezer) {
            logMessage('No freezer found', 'error');
            return;
        }

        const allWhitelistedAccounts = whitelist.map((wallet) => getAssociatedTokenAddressSync(new PublicKey(deployedToken), new PublicKey(wallet)).toBase58());
        setLPFreezeStarted(true);

        await freezeLpOwnersWithWhitelist(connection, allWhitelistedAccounts, freezer, deployedToken, freezerJitoTip, logMessage);
    }

    async function freezeWalletTokenAccount(connection: Connection, freezer: Keypair, wallet: string, token: string) {
        logMessage('Freezing wallet token account...');
        let tokenAccount: PublicKey;
        try {
            tokenAccount = (await getWalletTokenAccountByMint(connection, new PublicKey(wallet), new PublicKey(token)))[0].pubkey;
        } catch (error) {
            logMessage(`Token account not found`, 'error');
            return;
        }
        freezeTokenAccountsWithJito(connection, freezer, [tokenAccount], new PublicKey(deployedToken), freezerJitoTip, logMessage);
    }

    async function thawWalletTokenAccount(connection: Connection, freezer: Keypair, wallet: string, token: string) {
        logMessage('Thawing wallet token account...');
        let tokenAccount: PublicKey;
        try {
            tokenAccount = (await getWalletTokenAccountByMint(connection, new PublicKey(wallet), new PublicKey(token)))[0].pubkey;
        } catch (error) {
            logMessage(`Token account not found`, 'error');
            return;
        }
        await thawTokenAccountsWithJito(connection, freezer, [tokenAccount], [new PublicKey(deployedToken)], freezerJitoTip, logMessage);
    }

    return (
        <section>
            <div className='auto-freezer'>
                <p>Auto Freezer:</p>
                <button onClick={startFreezes} className='button-250' disabled={!deployedToken}>
                    Start Freezing
                </button>
            </div>
            <div className='freezer-container'>
                <input
                    type='text'
                    name='freezeUnfreezeInput'
                    placeholder='Wallet Token Account to Freeze / Unfreeze'
                    value={freezeUnfreezeInput}
                    onChange={freezeUnfreezeInputChange}
                    disabled={!deployedToken}
                />
                <button
                    onClick={() => {
                        freezeWalletTokenAccount(connection, freezer, freezeUnfreezeInput, deployedToken);
                    }}
                    className='button-200'
                    disabled={!deployedToken}
                >
                    Freeze
                </button>
                <button
                    onClick={() => {
                        thawWalletTokenAccount(connection, freezer, freezeUnfreezeInput, deployedToken);
                    }}
                    className='button-200'
                    disabled={!deployedToken}
                >
                    Unfreeze
                </button>
                <button
                    onClick={() => {
                        thawWalletTokenAccount(connection, freezer, raydiumAuthorityV4, deployedToken);
                    }}
                    className='button-200'
                    disabled={!deployedToken}
                >
                    Unfreeze Raydium
                </button>
            </div>
            <div className='auto-freezer'>
                <p>Smart Freezer:</p>
                <button
                    onClick={() => startSmartFreezes(connection, allWallets.concat(volumeBuyWallets), freezer, deployedToken)}
                    className='button-250'
                    disabled={!deployedToken}
                >
                    SMART FREEZER
                </button>
            </div>
            <div className='auto-freezer'>
                <p>LP Owners Freeze</p>
                <button
                    onClick={() => startLPFreezes(connection, allWallets.concat(volumeBuyWallets), freezer, deployedToken)}
                    className='button-250'
                    disabled={!deployedToken}
                >
                    START LP FREEZE
                </button>
            </div>
            <div>
                <p>Freezer Balance: {freezerBalance.toFixed(2)}</p>
            </div>
        </section>
    );
}
